/**
 * AI 服务
 * 提供 AI 相关的 API 功能
 */

import { SmartAgentAllocator } from '../../core/smart-agent-allocator.js';
import { TaskStore } from '../../core/task-store.js';
import { ExtendedTask } from '../../types/extended-task.js';
import { logger } from '../../utils/logger.js';
// TODO: 恢复使用 @gongfu/memory 包
import { MemoryClient, TaskMemoryAdapter, AgentMemoryAdapter } from '../../utils/memory-stub';

export interface AIServiceOptions {
  enableMemory?: boolean;
  userId?: string;
  projectId?: string;
}

export class AIService {
  private agentAllocator: SmartAgentAllocator;
  private taskStore: TaskStore;
  private taskMemory?: TaskMemoryAdapter;
  private agentMemory?: AgentMemoryAdapter;
  
  constructor(options?: AIServiceOptions) {
    this.taskStore = new TaskStore();
    
    // 初始化 Agent 分配器
    this.agentAllocator = new SmartAgentAllocator({
      enableMemory: options?.enableMemory,
      userId: options?.userId,
      projectId: options?.projectId,
    });
    
    // 初始化 Memory 适配器
    if (options?.enableMemory && options.userId && options.projectId) {
      try {
        this.taskMemory = new TaskMemoryAdapter({
          userId: options.userId,
          projectId: options.projectId,
        });
        this.agentMemory = new AgentMemoryAdapter({
          userId: options.userId,
          projectId: options.projectId,
        });
        logger.info('AI Service: Memory system initialized');
      } catch (error) {
        logger.warn('AI Service: Failed to initialize memory system', error);
      }
    }
  }
  
  /**
   * 推荐 Agent
   */
  async recommendAgent(taskId: string): Promise<any> {
    const task = await this.taskStore.getTaskById(taskId) as ExtendedTask;
    if (!task) {
      throw new Error('Task not found');
    }
    
    const allocation = await this.agentAllocator.allocateAgents(task);
    
    return {
      taskId,
      recommendation: {
        primary: allocation.primary,
        reviewers: allocation.reviewers || [],
        consultants: allocation.consultants || [],
        reason: allocation.reason,
        confidence: allocation.confidence,
        memoryBased: allocation.memoryBased || false,
      },
    };
  }
  
  /**
   * 批量推荐 Agent
   */
  async recommendAgentsForTasks(taskIds: string[]): Promise<any[]> {
    const recommendations = [];
    
    for (const taskId of taskIds) {
      try {
        const recommendation = await this.recommendAgent(taskId);
        recommendations.push(recommendation);
      } catch (error) {
        logger.error(`Failed to recommend agent for task ${taskId}:`, error);
        recommendations.push({
          taskId,
          error: error.message,
        });
      }
    }
    
    return recommendations;
  }
  
  /**
   * 获取任务建议
   */
  async getTaskSuggestions(description: string): Promise<any> {
    if (!this.taskMemory) {
      return {
        error: 'Memory system not enabled',
        suggestions: [],
      };
    }
    
    try {
      const suggestions = await this.taskMemory.suggestTask(description);
      return {
        description,
        suggestions,
      };
    } catch (error) {
      logger.error('Failed to get task suggestions:', error);
      return {
        description,
        error: error.message,
        suggestions: [],
      };
    }
  }
  
  /**
   * 记录 Agent 表现
   */
  async recordAgentPerformance(
    agentName: string,
    taskId: string,
    outcome: 'success' | 'failure' | 'reassigned'
  ): Promise<void> {
    const task = await this.taskStore.getTaskById(taskId) as ExtendedTask;
    if (!task) {
      throw new Error('Task not found');
    }
    
    // 如果有分配记录，记录到 allocator
    if (task.agentAssignments) {
      const allocation = {
        primary: task.agentAssignments.primary,
        reviewers: task.agentAssignments.reviewers,
        consultants: task.agentAssignments.consultants,
      };
      
      await this.agentAllocator.recordAllocation(task, allocation, outcome);
    }
    
    // 如果有 Memory 系统，记录更详细的信息
    if (this.agentMemory) {
      try {
        await this.agentMemory.recordAgentPerformance(
          agentName,
          task.content,
          {
            taskId,
            outcome,
            duration: task.completedAt && task.startedAt
              ? new Date(task.completedAt).getTime() - new Date(task.startedAt).getTime()
              : undefined,
            quality: outcome === 'success' ? 1 : 0,
          }
        );
      } catch (error) {
        logger.error('Failed to record agent performance to memory:', error);
      }
    }
  }
  
  /**
   * 获取 Agent 统计信息
   */
  async getAgentStats(): Promise<any> {
    const stats = await this.agentAllocator.getAllocationStats();
    
    // 如果有 Memory 系统，增强统计信息
    if (this.agentMemory) {
      try {
        const agentNames = Object.keys(stats).filter(k => k !== 'message' && k !== 'error');
        
        for (const agentName of agentNames) {
          const performance = await this.agentMemory.getAgentPerformance(agentName);
          if (performance) {
            stats[agentName] = {
              ...stats[agentName],
              memoryStats: {
                totalTasks: performance.totalTasks,
                successRate: performance.successRate,
                averageTime: performance.averageTime,
                specialties: performance.specialties,
                lastUsed: performance.lastUpdated,
              },
            };
          }
        }
      } catch (error) {
        logger.error('Failed to enhance agent stats with memory:', error);
      }
    }
    
    return stats;
  }
  
  /**
   * 获取 AI 功能状态
   */
  async getAIStatus(): Promise<any> {
    const status = {
      enabled: true,
      features: {
        agentAllocation: true,
        memorySystem: !!this.taskMemory,
        taskSuggestions: !!this.taskMemory,
        performanceTracking: !!this.agentMemory,
      },
      memoryConfig: this.taskMemory ? {
        userId: (this.taskMemory as any).userId,
        projectId: (this.taskMemory as any).projectId,
      } : null,
    };
    
    return status;
  }
}